import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Folder, FolderOpen, FileText, Image, Video, Music, Archive, File,
  Upload, Plus, Trash2, Download, Pencil, X, ChevronRight, ChevronDown,
  HardDrive, Clock, AlertCircle,
} from 'lucide-react';

// âââ Helpers ââââââââââââââââââââââââââââââââââââââââââââââââââââââ
const formatSize = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const fileIcon = (mimeType = '') => {
  if (mimeType.startsWith('image/')) return <Image size={18} color="#3b82f6" />;
  if (mimeType.startsWith('video/')) return <Video size={18} color="#a855f7" />;
  if (mimeType.startsWith('audio/')) return <Music size={18} color="#22c55e" />;
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('tar') || mimeType.includes('gz'))
    return <Archive size={18} color="#eab308" />;
  if (mimeType.includes('pdf') || mimeType.includes('word') || mimeType.includes('text') || mimeType.includes('document'))
    return <FileText size={18} color="#ef4444" />;
  return <File size={18} color="#a0a0a0" />;
};

const isExpired = (expiresAt) => expiresAt && new Date(expiresAt) < new Date();
const daysLeft = (expiresAt) => {
  if (!expiresAt) return null;
  const diff = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
};

// âââ Upload Modal âââââââââââââââââââââââââââââââââââââââââââââââââ
const UploadModal = ({ folderId, folderName, projects = [], invoices = [], onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [projectId, setProjectId] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef();

  const MAX_SIZE = 2 * 1024 * 1024 * 1024; // 2 GB
  const handleFile = (f) => {
    if (!f) return;
    if (f.size > MAX_SIZE) { alert('File is too large. Maximum allowed size is 2 GB.'); return; }
    setFile(f);
    setName(f.name);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name || file.name);
    if (expiresAt) fd.append('expiresAt', expiresAt);
    if (projectId) fd.append('projectId', projectId);
    if (invoiceId) fd.append('invoiceId', invoiceId);

    setUploading(true);
    try {
      await axios.post(`/api/drive/folders/${folderId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      onUploaded();
    } catch (err) { alert(err.response?.data?.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !uploading && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">Upload File</div>
          <button className="modal-close" onClick={onClose} disabled={uploading}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Drop zone */}
            <div
              onDrop={handleDrop} onDragOver={e => e.preventDefault()}
              onClick={() => inputRef.current?.click()}
              style={{
                border: `2px dashed ${file ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 10, padding: '28px 20px', textAlign: 'center',
                cursor: 'pointer', marginBottom: 16, transition: 'all 0.2s',
                background: file ? 'var(--accent-dim)' : 'var(--bg-secondary)',
              }}>
              <input ref={inputRef} type="file" style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])} />
              {file ? (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>{fileIcon(file.type)}</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{file.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{formatSize(file.size)}</div>
                </div>
              ) : (
                <div>
                  <Upload size={28} style={{ color: 'var(--text-muted)', marginBottom: 10 }} />
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Drop file here or click to browse</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>Max 2 GB · Any file type</div>
                </div>
              )}
            </div>

            {/* Display name */}
            <div className="input-group">
              <label className="input-label">Display Name</label>
              <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="File display name..." />
            </div>

            {/* Folder */}
            <div className="input-group">
              <label className="input-label">Destination Folder</label>
              <input className="input" value={folderName} disabled style={{ opacity: 0.7 }} />
            </div>

            <div className="grid-2">
              <div className="input-group">
                <label className="input-label">Linked Project</label>
                <select className="input select" value={projectId} onChange={e => setProjectId(e.target.value)}>
                  <option value="">No project link</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="input-group">
                <label className="input-label">Linked Invoice</label>
                <select className="input select" value={invoiceId} onChange={e => setInvoiceId(e.target.value)}>
                  <option value="">No invoice link</option>
                  {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.invoiceNumber}</option>)}
                </select>
              </div>
            </div>

            {/* Expiry */}
            <div className="input-group">
              <label className="input-label">Auto-Delete After (optional)</label>
              <input type="datetime-local" className="input" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Leave empty for permanent storage. File will be deleted automatically at this date/time.
              </div>
            </div>

            {/* Progress */}
            {uploading && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                  <span>Uploading...</span><span>{progress}%</span>
                </div>
                <div className="progress-bar" style={{ height: 6 }}>
                  <div className="progress-fill" style={{ width: `${progress}%`, transition: 'width 0.2s' }} />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={!file || uploading}>
                <Upload size={14} /> {uploading ? `${progress}%` : 'Upload'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// âââ New Folder Modal âââââââââââââââââââââââââââââââââââââââââââââ
const NewFolderModal = ({ clientId, parentId, parentName, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/drive/folders', { name: name.trim(), clientId, parentId: parentId || null });
      onCreated();
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <div className="modal-title">New Folder{parentName ? ` in "${parentName}"` : ''}</div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label className="input-label">Folder Name *</label>
              <input className="input" autoFocus required value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Design, Training, Website..." />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving || !name.trim()}>
                <Plus size={14} /> Create Folder
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// âââ Folder Tree Item âââââââââââââââââââââââââââââââââââââââââââââ
const FolderItem = ({ folder, selected, onSelect, depth = 0 }) => {
  const [open, setOpen] = useState(false);
  const hasSubs = folder.subfolders?.length > 0;
  const isSelected = selected?.id === folder.id;

  return (
    <div>
      <div
        onClick={() => { setOpen(o => !o); onSelect(folder); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: `7px ${12 + depth * 16}px`,
          cursor: 'pointer', borderRadius: 6, fontSize: 13, fontWeight: isSelected ? 600 : 400,
          background: isSelected ? 'var(--accent-dim)' : 'transparent',
          color: isSelected ? 'var(--accent)' : 'var(--text-secondary)',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-hover)'; }}
        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
        {hasSubs ? (
          open ? <ChevronDown size={13} /> : <ChevronRight size={13} />
        ) : <span style={{ width: 13 }} />}
        {isSelected ? <FolderOpen size={15} color="var(--accent)" /> : <Folder size={15} />}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder.name}</span>
        {(folder.files?.length || 0) > 0 && (
          <span style={{ fontSize: 10, background: 'var(--bg-hover)', borderRadius: 8, padding: '1px 6px', color: 'var(--text-muted)' }}>
            {folder.files.length}
          </span>
        )}
      </div>
      {open && hasSubs && folder.subfolders.map(sub => (
        <FolderItem key={sub.id} folder={sub} selected={selected} onSelect={onSelect} depth={depth + 1} />
      ))}
    </div>
  );
};

// âââ File Card âââââââââââââââââââââââââââââââââââââââââââââââââââââ
const FileCard = ({ file, canManage, onDelete, onRename, onDownload }) => {
  const expired = isExpired(file.expiresAt);
  const days = daysLeft(file.expiresAt);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
      background: 'var(--bg-secondary)', borderRadius: 10, marginBottom: 8,
      border: `1px solid ${expired ? 'rgba(239,68,68,0.3)' : days !== null && days <= 2 ? 'rgba(234,179,8,0.4)' : 'var(--border)'}`,
      opacity: expired ? 0.6 : 1,
    }}>
      {/* Icon */}
      <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 8, background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)' }}>
        {fileIcon(file.mimeType)}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.name}
          {expired && <span className="badge badge-red" style={{ fontSize: 10, marginLeft: 6 }}>Expired</span>}
        </div>
        <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'var(--text-muted)', marginTop: 3, flexWrap: 'wrap' }}>
          <span>{formatSize(file.size)}</span>
          {file.uploader && <span>by {file.uploader.name}</span>}
          <span>{new Date(file.createdAt).toLocaleDateString()}</span>
          {file.downloadCount > 0 && <span>{file.downloadCount} download{file.downloadCount !== 1 ? 's' : ''}</span>}
          {file.project && <span>Project: {file.project.name}</span>}
          {file.invoice && <span>Invoice: {file.invoice.invoiceNumber}</span>}
        </div>

        {/* Expiry indicator */}
        {file.expiresAt && !expired && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 11,
            color: days <= 2 ? '#ef4444' : days <= 7 ? '#eab308' : 'var(--text-muted)' }}>
            {days <= 2 ? <AlertCircle size={11} /> : <Clock size={11} />}
            {days === 0 ? 'Expires today' : days === 1 ? 'Expires tomorrow' : `Expires in ${days} days`}
            <span style={{ opacity: 0.7 }}>· {new Date(file.expiresAt).toLocaleDateString()}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        {!expired && (
          <button onClick={() => onDownload(file)} title="Download"
            style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: '6px 8px', borderRadius: 6,
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <Download size={14} />
          </button>
        )}
        {canManage && (
          <>
            <button onClick={() => onRename(file)} title="Rename / Set expiry"
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '6px 8px', borderRadius: 6 }}>
              <Pencil size={13} />
            </button>
            <button onClick={() => onDelete(file)} title="Delete"
              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px 8px', borderRadius: 6 }}>
              <Trash2 size={13} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// âââ Rename/Edit File Modal ââââââââââââââââââââââââââââââââââââââââ
const EditFileModal = ({ file, onClose, onSaved }) => {
  const [name, setName] = useState(file.name);
  const [expiresAt, setExpiresAt] = useState(
    file.expiresAt ? new Date(file.expiresAt).toISOString().slice(0, 16) : ''
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`/api/drive/files/${file.id}`, { name, expiresAt: expiresAt || null });
      onSaved();
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <div className="modal-title">Edit File</div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label className="input-label">Display Name</label>
              <input className="input" value={name} onChange={e => setName(e.target.value)} required />
            </div>
            <div className="input-group">
              <label className="input-label">Auto-Delete At (optional)</label>
              <input type="datetime-local" className="input" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
              {expiresAt && <div style={{ fontSize: 12, color: '#eab308', marginTop: 4 }}>â ï¸ File will be permanently deleted at this time</div>}
              {!expiresAt && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Leave empty for permanent storage</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>Save</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// âââ Main Drive Page ââââââââââââââââââââââââââââââââââââââââââââââ
const Drive = () => {
  const { user } = useAuth();
  const canManage = ['admin', 'manager'].includes(user?.role);
  const isClient = user?.role === 'client';

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [linkProjects, setLinkProjects] = useState([]);
  const [linkInvoices, setLinkInvoices] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderDetail, setFolderDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // Modals
  const [uploadModal, setUploadModal] = useState(false);
  const [newFolderModal, setNewFolderModal] = useState(null); // { parentId, parentName }
  const [editFileModal, setEditFileModal] = useState(null);

  // ââ Load client list (admin) or own drive (client) âââââââââââââââ
  useEffect(() => {
    if (isClient) {
      loadMyDrive();
    } else {
      loadSummary();
    }
  }, []);

  const loadMyDrive = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/drive/my');
      setFolders(r.data.data.folders || []);
      setSelectedClientId(r.data.data.clientId);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadSummary = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/drive/summary');
      setClients(r.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // ââ Load folders for selected client (admin) ââââââââââââââââââââââ
  const loadClientFolders = async (clientId) => {
    setFolders([]);
    setLinkProjects([]);
    setLinkInvoices([]);
    setSelectedFolder(null);
    setFolderDetail(null);
    try {
      const [foldersRes, clientRes] = await Promise.all([
        axios.get(`/api/drive/clients/${clientId}/folders`),
        axios.get(`/api/clients/${clientId}`),
      ]);
      setFolders(foldersRes.data.data);
      setLinkProjects(clientRes.data.data.projects || []);
      setLinkInvoices(clientRes.data.data.invoices || []);
    } catch (err) { console.error(err); }
  };

  // ââ Load folder detail (files + subfolders) ââââââââââââââââââââââââ
  const loadFolderDetail = async (folder) => {
    if (!folder) return;
    setLoadingFiles(true);
    try {
      const r = await axios.get(`/api/drive/folders/${folder.id}`);
      setFolderDetail(r.data.data);
    } catch (err) { console.error(err); }
    finally { setLoadingFiles(false); }
  };

  const handleSelectFolder = (folder) => {
    setSelectedFolder(folder);
    loadFolderDetail(folder);
  };

  const handleSelectClient = (clientId) => {
    setSelectedClientId(clientId);
    loadClientFolders(clientId);
  };

  const handleDownload = async (file) => {
    try {
      const r = await axios.get(`/api/drive/files/${file.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalName || file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      // Refresh to update download count
      if (selectedFolder) loadFolderDetail(selectedFolder);
    } catch (err) { alert('Download failed or file has expired'); }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Delete "${file.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/drive/files/${file.id}`);
      loadFolderDetail(selectedFolder);
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}" and all its files? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/drive/folders/${folder.id}`);
      setSelectedFolder(null);
      setFolderDetail(null);
      if (isClient) loadMyDrive();
      else loadClientFolders(selectedClientId);
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const onCreated = () => {
    setNewFolderModal(null);
    if (isClient) loadMyDrive();
    else loadClientFolders(selectedClientId);
  };

  const onUploaded = () => {
    setUploadModal(false);
    loadFolderDetail(selectedFolder);
    if (isClient) loadMyDrive();
    else loadClientFolders(selectedClientId);
  };

  const onFileSaved = () => {
    setEditFileModal(null);
    loadFolderDetail(selectedFolder);
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;

  // ââ Current files list ââââââââââââââââââââââââââââââââââââââââââââ
  const currentFiles = folderDetail?.files || [];
  const currentSubfolders = folderDetail?.subfolders || [];

  return (
    <div className="page-content" style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Modals */}
      {uploadModal && selectedFolder && (
        <UploadModal
          folderId={selectedFolder.id}
          folderName={selectedFolder.name}
          projects={linkProjects}
          invoices={linkInvoices}
          onClose={() => setUploadModal(false)}
          onUploaded={onUploaded}
        />
      )}
      {newFolderModal && (
        <NewFolderModal
          clientId={selectedClientId}
          parentId={newFolderModal.parentId}
          parentName={newFolderModal.parentName}
          onClose={() => setNewFolderModal(null)}
          onCreated={onCreated}
        />
      )}
      {editFileModal && (
        <EditFileModal
          file={editFileModal}
          onClose={() => setEditFileModal(null)}
          onSaved={onFileSaved}
        />
      )}

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* ââ Left Sidebar âââââââââââââââââââââââââââââââââââââââââââ */}
        <div className="drive-sidebar">
          <div style={{ padding: '20px 16px 12px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <HardDrive size={16} color="var(--accent)" />
              <span style={{ fontSize: 14, fontWeight: 700 }}>Drive</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {isClient ? 'Your files' : `${clients.length} clients`}
            </div>
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {/* Admin: client list */}
            {canManage && (
              <div>
                <div style={{ padding: '10px 16px 4px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Clients</div>
                {clients.map(c => (
                  <div key={c.id}>
                    <div
                      onClick={() => handleSelectClient(c.id)}
                      style={{
                        padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: selectedClientId === c.id ? 600 : 400,
                        color: selectedClientId === c.id ? 'var(--accent)' : 'var(--text-secondary)',
                        background: selectedClientId === c.id ? 'var(--accent-dim)' : 'transparent',
                        borderLeft: selectedClientId === c.id ? '3px solid var(--accent)' : '3px solid transparent',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { if (selectedClientId !== c.id) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={e => { if (selectedClientId !== c.id) e.currentTarget.style.background = 'transparent'; }}>
                      <div>{c.companyName}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                        {c.folderCount} folder{c.folderCount !== 1 ? 's' : ''} · {c.fileCount} file{c.fileCount !== 1 ? 's' : ''} · {c.totalSize}
                      </div>
                    </div>

                    {/* Folders under this client */}
                    {selectedClientId === c.id && (
                      <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8, marginBottom: 4 }}>
                        {folders.map(folder => (
                          <FolderItem key={folder.id} folder={folder} selected={selectedFolder} onSelect={handleSelectFolder} depth={1} />
                        ))}
                        <button
                          onClick={() => setNewFolderModal({ parentId: null, parentName: null })}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 28px', background: 'none', border: 'none',
                            color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, width: '100%' }}>
                          <Plus size={12} /> New Folder
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Client: own folders */}
            {isClient && (
              <div style={{ padding: '8px 0' }}>
                <div style={{ padding: '4px 16px 8px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>My Folders</div>
                {folders.map(folder => (
                  <FolderItem key={folder.id} folder={folder} selected={selectedFolder} onSelect={handleSelectFolder} />
                ))}
                {!folders.length && (
                  <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)' }}>No folders yet</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ââ Main Content ââââââââââââââââââââââââââââââââââââââââââââ */}
        <div className="drive-content">
          {!selectedFolder ? (
            /* Empty state */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
              <FolderOpen size={52} style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
              <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-muted)' }}>
                {canManage && !selectedClientId ? 'Select a client to view their drive' : 'Select a folder to view files'}
              </div>
              {canManage && selectedClientId && !folders.length && (
                <button className="btn btn-primary" onClick={() => setNewFolderModal({ parentId: null, parentName: null })}>
                  <Plus size={14} /> Create First Folder
                </button>
              )}
            </div>
          ) : (
            <div style={{ padding: 24, height: '100%', overflowY: 'auto' }}>
              {/* Folder header */}
              <div className="flex-between" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <FolderOpen size={22} color="var(--accent)" />
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700 }}>{selectedFolder.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {currentFiles.length} file{currentFiles.length !== 1 ? 's' : ''}
                      {currentSubfolders.length > 0 && ` · ${currentSubfolders.length} subfolder${currentSubfolders.length !== 1 ? 's' : ''}`}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {canManage && (
                    <>
                      <button className="btn btn-secondary btn-sm"
                        onClick={() => setNewFolderModal({ parentId: selectedFolder.id, parentName: selectedFolder.name })}>
                        <Plus size={13} /> Subfolder
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={() => setUploadModal(true)}>
                        <Upload size={13} /> Upload
                      </button>
                      <button onClick={() => handleDeleteFolder(selectedFolder)} title="Delete folder"
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px 8px' }}>
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {loadingFiles ? (
                <div style={{ color: 'var(--text-muted)', padding: 20 }}>Loading...</div>
              ) : (
                <>
                  {/* Subfolders */}
                  {currentSubfolders.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>Subfolders</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                        {currentSubfolders.map(sub => (
                          <div key={sub.id}
                            onClick={() => handleSelectFolder(sub)}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)',
                              cursor: 'pointer', background: 'var(--bg-secondary)', transition: 'all 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-light)'}
                            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                            <Folder size={18} color="#eab308" />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub.files?.length || 0} files</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Files */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>Files</div>
                    {!currentFiles.length ? (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                        <Upload size={32} style={{ opacity: 0.3, marginBottom: 10 }} />
                        <div style={{ fontSize: 14 }}>No files yet</div>
                        {canManage && (
                          <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => setUploadModal(true)}>
                            <Upload size={13} /> Upload First File
                          </button>
                        )}
                      </div>
                    ) : (
                      currentFiles.map(file => (
                        <FileCard
                          key={file.id}
                          file={file}
                          canManage={canManage}
                          onDownload={handleDownload}
                          onDelete={handleDeleteFile}
                          onRename={setEditFileModal}
                        />
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Drive;
